<template>
  <div class="goal-scope">
    <div
      v-if="!hideOp"
      class="_op"
    >
      <m-select
        v-model:value="selectedType"
        :items="operatorTypes"
        :read-only="readOnly"
        :disabled="disabled"
        class="_select"
      />
    </div>
    <goal-picker
      v-show="!isEmpty"
      :value="selectedGoalUids"
      class="_goal-select"
      :placeholder="$t('general.empty')"
      :default-view="defaultView"
      :view-application="goalParentPicker"
      :initial-selected-cycles="selectedCycles"
      :base-filter="baseFilter"
      show-create-view
      show-views-selector
      full-width
      :read-only="readOnly"
      placement="bottomCenter"
      multiple
      show-input-field
      @update:value="updateGoals"
    />
  </div>
</template>

<script>
import useGoalDefaultProps from '@/composables/saved-views/goal-default-props';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePersistedGoalCycle from '@/composables/goal-cycle/persisted-goal-cycle';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useScopeItem from '@/composables/filter/scope-item';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { GOAL_RELATION } from '@/lib/props/custom-types';
import { copy } from 'shared/lib/copy';
import { defineAsyncComponent, ref } from 'vue';
import { viewApplication } from 'shared/constants.json';

export default {
  name: 'GoalScope',
  props: {
    value: {
      type: Object,
      required: true,
    },
    isNot: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    hideIsEmpty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:value', 'change-not', 'change'],
  setup(props) {
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const defaultPropsSvc = useGoalDefaultProps();
    const defaultView = ref({
      params: {
        filter: { account: { uid: loggedInUserAccount.value.uid }, children: [], op: AND },
        order: [],
        props: defaultPropsSvc.defaultProps.value,
        applyFilterOnFirstLevelOnly: false,
      },
    });
    const { publishedAtFilter: baseFilter } = usePublishedAtFilter();
    const { composedSelectedCycles: persistedSelectedCycles } = usePersistedGoalCycle();
    const selectedCycles = ref(persistedSelectedCycles);

    const { operatorTypes: getOperatorTypes } = useScopeItem();
    const operatorTypes = getOperatorTypes(GOAL_RELATION, { hideEmpty: props.hideIsEmpty });
    return { defaultView, baseFilter, selectedCycles, operatorTypes };
  },
  components: { GoalPicker: defineAsyncComponent(() => import('@/components/goal/GoalPicker.vue')) },
  data() {
    return {
      goalParentPicker: viewApplication.goalParentPicker,
      selectedGoals: [],
      isEmpty: false,
    };
  },
  computed: {
    selectedGoalUids() {
      return this.selectedGoals.map((g) => g.uid);
    },
    selectedType: {
      get() {
        if (this.isNot) {
          if (this.isEmpty) {
            return NOT_EMPTY;
          }
          return NOT;
        }
        if (this.isEmpty) {
          return EMPTY;
        }
        return AND;
      },
      set(val) {
        this.updateEmpty(val);
        if ([NOT, NOT_EMPTY].includes(val)) {
          this.$emit('change-not', NOT);
          return;
        }
        this.$emit('change-not', AND);
      },
    },
    scope() {
      return {
        ...this.value,
        relations: this.selectedGoals,
        isEmpty: this.isEmpty,
      };
    },
  },
  methods: {
    updateGoals(val) {
      this.selectedGoals = val.map((uid) => ({ uid }));
    },
    updateEmpty(type) {
      if ([EMPTY, NOT_EMPTY].includes(type)) {
        this.isEmpty = true;
        return;
      }
      this.isEmpty = false;
    },
    updateValues() {
      let isEmpty = false;
      if (this.value !== null && this.value.isEmpty !== undefined) {
        isEmpty = this.value.isEmpty;
      }
      this.isEmpty = isEmpty;

      if (this.value === null || !Array.isArray(this.value.relations)) {
        this.selectedGoals = [];
        return;
      }
      this.selectedGoals = this.value.relations;
    },
    emitUpdate(val) {
      this.$emit('input', val);
      this.$emit('update:value', val);
      this.$emit('change', val);
    },
  },
  watch: {
    scope: {
      handler(newVal, oldVal) {
        if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
          return;
        }

        this.emitUpdate(copy(newVal));
      },
      deep: true,
    },
    value: {
      handler() {
        this.updateValues();
      },
      deep: true,
    },
  },
  created() {
    this.updateValues();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .goal-scope {
    display: flex;
    max-width: 30rem;

    ._op {
      margin-right: .8rem;

      ._select {
        max-width: 100%;
      }
    }

    ._goal-select {
      min-width: 20rem;
    }
  }
</style>
